import { useEventListener } from '@vueuse/core';
import { watch, type Ref } from 'vue';
import type { SearchCriteria } from '@/entities/searchCriteria';
import { getCurrentUrl } from '@/shared/lib/urlQuery';
import type { FiltersModel, SerpSort } from './types';
import { toSerpUrl } from '../lib/serpUrl';

export interface SerpUrlOptions {
  criteria: Ref<SearchCriteria>;
  filtersModel: Ref<FiltersModel>;
  sort: Ref<SerpSort>;
  searchId: Ref<string>;
}

export function useSerpUrl({ criteria, filtersModel, sort, searchId }: SerpUrlOptions): void {
  const isHydrated = (): boolean => {
    const { destination } = criteria.value;

    return Boolean(destination) && destination?.kind !== 'none';
  };

  const getStateUrl = (): string =>
    toSerpUrl(getCurrentUrl(), {
      criteria: criteria.value,
      filtersModel: filtersModel.value,
      sort: sort.value,
      searchId: searchId.value,
    });

  const syncUrl = (): void => {
    if (typeof window === 'undefined' || !isHydrated()) return;

    const url = getStateUrl();

    if (url !== window.location.href) {
      window.history.replaceState(null, '', url);
    }
  };

  const reloadOnForeignUrl = (): void => {
    if (isHydrated() && getStateUrl() === window.location.href) return;

    window.location.reload();
  };

  watch(criteria, syncUrl, { immediate: true });
  watch(filtersModel, syncUrl, { deep: true });
  watch(sort, syncUrl);
  watch(searchId, syncUrl);

  useEventListener('hashchange', reloadOnForeignUrl);
}
